import { Location, useLocation, useNavigate } from 'react-router-dom';
import { Controller, FormContainer, useFormContext } from 'react-hook-form-mui';
import { JobPostHeader, ScrollableWrapper } from 'view/pages/createJobPost/components';
import { BackButton, Button, Input, Spinner } from 'view/components';
import { useUpdateJobPostByStep } from 'view/pages/createJobPost/hooks/useUpdateJobPostByStep';
import TestSelect from 'view/pages/profile/TestPage/Select';
import { PreviewIcon, AddIcon, EditIcon, DeletePurpleIcon } from 'assets';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { useContextSelector as useCS } from 'use-context-selector';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { TUpdate } from 'domain/types';
import * as CS from 'view/pages/createJobPost/commonStyled';
import { Alert, useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import Step from '@mui/material/Step';
import { steps } from 'view/layouts/CreateJobPostLayout';
import { CreateJobPostBackToHomeModal } from 'view/modals';
import { useStateMachine } from 'little-state-machine';
import { updateStep5Action } from 'view/pages/createJobPost/helpers/updateStep5Action';
import { useEffect } from 'react';
import { resetForm } from 'view/pages/createJobPost/helpers/resetForm';
import { updateFAQ } from 'view/pages/createJobPost/helpers/updateFAQ';
import * as S from './styled';

const immHiringNeedOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
];

interface IFormInput {
  applicationProcess: string;
  immHiringNeed: any;
  diversityStatement: string;
}

const ApplicationInstructionsFields = ({
  isUpdating,
  publishError,
  onDraftSubmit,
  onPreviewSubmit,
  companyIsPending,
}) => {
  const location: Location = useLocation();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));

  const { actions } = useStateMachine({
    actions: { updateFAQ },
  });

  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );
  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );

  const screeningQuestions = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.screeningQuestions
  );

  const activeStep = steps.findIndex((step) => {
    return location.pathname.includes(step.path);
  });

  const navigate = useNavigate();
  const {
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<IFormInput>();

  const applicationProcessValue: any = watch('applicationProcess') ?? null;
  const immHiringNeedValue: any = watch('immHiringNeed') ?? null;

  return (
    <>
      <CS.CreateJobPostHeader style={{ justifyContent: 'space-between' }}>
        {matches ? (
          <CS.CJPBackButtonBox>
            <BackButton
              text="Back to Home"
              color="#663497"
              onClick={() => {
                if (update) update({ isOpenBackToHomeModal: true });
              }}
            />
          </CS.CJPBackButtonBox>
        ) : null}
        <CS.CreateJobPostHeader>
          <Button
            variant="outlined"
            text="Save as Draft"
            onClick={handleSubmit(onDraftSubmit)}
            style={{ padding: '8px 20px', width: 'max-content' }}
            disabled={
              !!Object.keys(errors).length ||
              !applicationProcessValue ||
              !immHiringNeedValue ||
              isUpdating
            }
          />
          <Button
            variant="outlined"
            text={matches ? '' : 'Preview'}
            onClick={handleSubmit(onPreviewSubmit)}
            endIcon={
              <PreviewIcon
                disabled={
                  !!Object.keys(errors).length ||
                  !applicationProcessValue ||
                  !immHiringNeedValue ||
                  isUpdating
                }
              />
            }
            style={{ padding: '8px 20px', width: 'max-content' }}
            disabled={
              !!Object.keys(errors).length ||
              !applicationProcessValue ||
              !immHiringNeedValue ||
              isUpdating
            }
          />
        </CS.CreateJobPostHeader>
      </CS.CreateJobPostHeader>
      {matches ? (
        <div>
          <JobPostHeader
            title={`${createJobPostData?.editStatus ? 'Edit' : 'Create'} Job Post`}
            style={{ marginBottom: '24px', width: 'max-content' }}
          />
          <CS.CustomStepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
            {steps.map((step) => {
              return (
                <Step key={step.label}>
                  <CS.StepLabelText />
                </Step>
              );
            })}
          </CS.CustomStepper>
        </div>
      ) : null}

      <ScrollableWrapper>
        <CS.CreateJobPostMainWrapper>
          <JobPostHeader
            title="Application Instructions"
            subtitle="Tell applicants how to apply and what to expect next."
          />
          <Input
            name="applicationProcess"
            label="Application Process *"
            subLabel="Specify if you need a resume, cover letter, portfolio, or assessments."
            placeholder="Enter specific instructions..."
            multiline
            rows={6}
            marginbottom={16}
          />
          <Controller
            name="immHiringNeed"
            control={control}
            render={({ field }) => {
              return (
                <TestSelect
                  {...field}
                  label="Immediate Hiring Need *"
                  options={immHiringNeedOptions}
                  selectedValue={field.value}
                  onChange={(selected) => {
                    return field.onChange(selected);
                  }}
                />
              );
            }}
          />
          <Input
            name="diversityStatement"
            label="Diversity and Inclusion Statement "
            placeholder="Diversity and inclusion statement..."
            multiline
            rows={6}
            marginbottom={16}
          />

          <S.ScreeningQuestionsBlock>
            <S.ScreeningQuestionsBlockLabel>FAQ</S.ScreeningQuestionsBlockLabel>
            {screeningQuestions.length > 0
              ? screeningQuestions.map((el) => {
                  return (
                    <S.ScreeningQuestionsBlockQuestion key={el.id}>
                      <S.ScreeningQuestionsBlockQuestionText>
                        {el.question}
                      </S.ScreeningQuestionsBlockQuestionText>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <EditIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (update)
                              update({
                                editingQuestionId: el.id,
                                isOpenAddQuestionModal: true,
                              });
                          }}
                        />
                        <DeletePurpleIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const filteredItems = screeningQuestions.filter((item) => {
                              return item.id !== el.id;
                            });
                            if (update)
                              update({
                                screeningQuestions: filteredItems,
                              });
                            actions.updateFAQ({ faq: filteredItems });
                          }}
                        />
                      </div>
                    </S.ScreeningQuestionsBlockQuestion>
                  );
                })
              : null}

            <S.ScreeningQuestionsBlockAddBox
              onClick={() => {
                if (update) update({ isOpenAddQuestionModal: true });
              }}
            >
              <AddIcon />
              <p>Add question</p>
            </S.ScreeningQuestionsBlockAddBox>
          </S.ScreeningQuestionsBlock>

          <CS.CJPSubmitBtnWrapper>
            <Button
              variant="outlined"
              text="Back"
              onClick={() => {
                return navigate('/create-job-post/step3', { replace: true });
              }}
              style={{ margin: '32px 0' }}
            />
            <Button
              variant="contained"
              type="submit"
              text="Post Job"
              style={{ margin: '32px 0' }}
              endIcon={
                isUpdating ? (
                  <Spinner
                    disabled={!!Object.keys(errors).length || isUpdating}
                    variant="contained"
                  />
                ) : null
              }
              disabled={
                !!Object.keys(errors).length ||
                !applicationProcessValue ||
                !immHiringNeedValue ||
                companyIsPending ||
                isUpdating
              }
            />
          </CS.CJPSubmitBtnWrapper>
          {companyIsPending && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your company is pending review and cannot be used until an administrator approves it.
            </Alert>
          )}
          {publishError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {publishError?.response?.data?.message ||
                'The job post could not be published. Please try again.'}
            </Alert>
          )}
        </CS.CreateJobPostMainWrapper>
      </ScrollableWrapper>
      <CreateJobPostBackToHomeModal
        onDraftSubmitClick={handleSubmit(onDraftSubmit)}
        endIcon={
          isUpdating ? (
            <Spinner disabled={!!Object.keys(errors).length || isUpdating} variant="contained" />
          ) : null
        }
        disabled={
          !!Object.keys(errors).length ||
          !applicationProcessValue ||
          !immHiringNeedValue ||
          isUpdating
        }
      />
    </>
  );
};

const ApplicationInstructions = () => {
  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const createJobPostData: any = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );

  const screeningQuestions: any = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.screeningQuestions
  );

  const { state, actions } = useStateMachine({
    actions: { updateStep5Action, resetForm },
  });

  useEffect(() => {
    if (state.data?.faq?.length > 0 && update) {
      update({ screeningQuestions: state.data?.faq });
    }
  }, [state, screeningQuestions]);

  const {
    isLoading: isUpdating,
    error: publishError,
    updateJobPostByStep,
  } = useUpdateJobPostByStep('/create-job-post/step5');

  const getDataToSubmit = (formData, isDraft) => {
    /* const isDateBasedId = (id) => {
      return /^\d{13}$/.test(id);
    }; */
    return {
      applicationProcess: formData?.applicationProcess,
      immHiringNeed: formData?.immHiringNeed?.name === 'Yes',
      diversityStatement: formData?.diversityStatement,
      /* faq:
        screeningQuestions.length > 0
          ? screeningQuestions.map((el) => {
              return isDateBasedId(el.id) ? { ...el, id: null } : el;
            })
          : [], */
      faq: screeningQuestions.length > 0 ? screeningQuestions : [],
      progressStep: 5,
      postStatus: isDraft ? 'draft' : 'active',
    };
  };

  const onSubmit = async (formData: IFormInput) => {
    const isDateBasedId = (id) => {
      return /^\d{13}$/.test(id);
    };
    const dataToSend = getDataToSubmit(formData, false);
    actions.updateStep5Action(dataToSend);
    updateJobPostByStep(
      {
        ...state.data,
        ...dataToSend,
        faq:
          state.data?.faq?.length > 0
            ? state.data?.faq?.map((el) => {
                return isDateBasedId(el.id) ? { ...el, id: null } : el;
              })
            : [],
      },
      false
    );
  };

  const onDraftSubmit = (formData: IFormInput) => {
    const isDateBasedId = (id) => {
      return /^\d{13}$/.test(id);
    };
    const dataToSend = getDataToSubmit(formData, true);
    const dataToSendWithoutEmptyValues = JSON.parse(
      JSON.stringify({ ...state.data, ...dataToSend })
    );
    dataToSendWithoutEmptyValues.immHiringNeed =
      dataToSendWithoutEmptyValues?.immHiringNeed?.name === 'Yes';
    for (const key in dataToSendWithoutEmptyValues) {
      if (dataToSendWithoutEmptyValues[key] === null || dataToSendWithoutEmptyValues[key] === '') {
        delete dataToSendWithoutEmptyValues[key];
      }
    }
    updateJobPostByStep(
      {
        ...dataToSendWithoutEmptyValues,
        editStatus: null,
        postStatus: 'draft',
        faq:
          state.data?.faq?.length > 0
            ? state.data?.faq?.map((el) => {
                return isDateBasedId(el.id) ? { ...el, id: null } : el;
              })
            : [],
      },
      true
    );
  };

  const onPreviewSubmit = (formData: IFormInput) => {
    const isDateBasedId = (id) => {
      return /^\d{13}$/.test(id);
    };
    const dataToSend = getDataToSubmit(formData, true);
    const dataToSendWithoutEmptyValues = JSON.parse(
      JSON.stringify({ ...state.data, ...dataToSend })
    );
    dataToSendWithoutEmptyValues.immHiringNeed =
      dataToSendWithoutEmptyValues?.immHiringNeed?.name === 'Yes';
    for (const key in dataToSendWithoutEmptyValues) {
      if (dataToSendWithoutEmptyValues[key] === null || dataToSendWithoutEmptyValues[key] === '') {
        delete dataToSendWithoutEmptyValues[key];
      }
    }
    actions.updateStep5Action(dataToSendWithoutEmptyValues);
    updateJobPostByStep(
      {
        ...dataToSendWithoutEmptyValues,
        faq:
          state.data?.faq?.length > 0
            ? state.data?.faq?.map((el) => {
                return isDateBasedId(el.id) ? { ...el, id: null } : el;
              })
            : [],
      },
      true,
      true
    );
  };

  const defaultValues = {
    applicationProcess:
      state.data.applicationProcess?.trim() || createJobPostData?.applicationProcess?.trim() || '',
    immHiringNeed:
      state.data.immHiringNeed || createJobPostData?.immHiringNeed
        ? { id: 1, name: 'Yes' }
        : { id: 2, name: 'No' },
    diversityStatement:
      state.data.diversityStatement?.trim() || createJobPostData?.diversityStatement?.trim() || '',
  };

  return (
    <>
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit}
      >
        <ApplicationInstructionsFields
          isUpdating={isUpdating}
          publishError={publishError}
          onDraftSubmit={onDraftSubmit}
          onPreviewSubmit={onPreviewSubmit}
          companyIsPending={
            ((state.data as any)?.company?.status || createJobPostData?.company?.status) ===
            'PENDING_REVIEW'
          }
        />
      </FormContainer>
    </>
  );
};

export default ApplicationInstructions;
